import React, { FC, useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './thoka.css';

/**
 * Copy for the Thoka moment. `announcement` is what assistive tech hears;
 * the visible chip shows `title` + `detail`.
 */
export const THOKA_FEEDBACK = {
  title: 'Thoka',
  detail: 'pile returns',
  announcement: 'Thoka. Someone broke suit, so the pile returns to a hand.',
} as const;

/** The whole motion sequence; it must stay under 900ms. */
export const THOKA_DURATION_S = 0.82;

/**
 * Thoka feedback: a felt-and-ivory chip that settles above the pile while
 * three small card backs slide back toward a hand. It plays once (no loops,
 * flashes or particles), never blocks taps, and holds still afterwards.
 * Framer's app-level `MotionConfig reducedMotion="user"` drops transforms;
 * we also skip the decorative slide entirely when reduced motion is on.
 * Behaviour is unchanged: it is still mounted while `turnRes.thoka` is set.
 */
export const Thoka: FC = () => {
  const reduceMotion = useReducedMotion();
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    setAnnouncement(THOKA_FEEDBACK.announcement);
  }, []);

  return (
    <div className="bh-thoka" data-testid="thoka">
      <motion.div
        className="bh-thoka__chip"
        initial={reduceMotion ? false : { opacity: 0, y: 10, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
      >
        <span className="bh-thoka__stack" aria-hidden="true">
          {[0, 1, 2].map(i => (
            <motion.span
              key={i}
              className="bh-thoka__card"
              initial={reduceMotion ? false : { x: 0, y: 0, opacity: 1 }}
              animate={
                reduceMotion
                  ? { x: i * 3, y: -i * 2, opacity: 1 }
                  : { x: [0, i * 3 - 14, i * 3], y: [0, 6, -i * 2], opacity: 1 }
              }
              transition={{
                duration: THOKA_DURATION_S - 0.3,
                delay: 0.12 + i * 0.06,
                ease: 'easeOut',
              }}
            />
          ))}
        </span>
        <span className="bh-thoka__text" aria-hidden="true">
          <span className="bh-thoka__title">{THOKA_FEEDBACK.title}</span>
          <span className="bh-thoka__sep">·</span>
          <span className="bh-thoka__detail">{THOKA_FEEDBACK.detail}</span>
        </span>
      </motion.div>
      <span
        className="bh-thoka__sr"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </span>
    </div>
  );
};
